import type { BondOrder } from '../domain/editor';
import { placeBondStereoLabels, placeChargeLabels, placeStereoLabels } from '../chemistry/chargePlacement';
import { contractedDisplay } from '../domain/contractedGroups.ts';
import { mapPathControls, pathData } from '../domain/pathEditing';
import { calloutOutline, calloutTextLayout } from './calloutGeometry';
import { isBackgroundShape } from '../domain/annotationRendering';
import { fusedRingDoubleBondSides, nonFusedRingDoubleBondCenters, shapeExtentPoints } from '../domain/objectEditing';
import { pointsToUnits } from '../domain/page.ts';
import { atomLabelColor } from '../domain/moleculeAppearance.ts';
import {
  DEFAULT_BOND_DRAWING_CONFIG,
  boundedBondStrokeWidthPx,
  distanceBetween,
  normalizedToPixel,
  typicalBondLengthPx,
  type PixelPoint
} from '../domain/bonds.ts';
import { roundToOneDecimal, type PaperSize } from '../domain/geometry.ts';
import { repeatUnitBrackets, type RepeatUnitRef } from '../domain/markushRepeatBrackets.ts';
import {
  aromaticRingAtomPaths,
  documentToAtoms,
  documentToLines,
  withAtomLabelOverrides
} from '../domain/graphDrawing.ts';
import type { DrawingDocument } from '../model';
import { isChemicalBond, type BondStereoValue } from '../domain/documentSelectors.ts';
import {
  elementSymbol,
  formalChargeLabel,
  formalChargeLabelOffsetX,
  implicitHydrogenLabel,
  isotopeSuperscript,
  radicalLabel,
  resonanceChargeLabel
} from '../chemistry/elements.ts';
import { atomLabelBox, atomShowsLabel, type AtomLabelBox } from '../chemistry/labelMetrics.ts';
import {
  LABEL_BOND_GAP_PX,
  atomLabelHulls,
  clipToLabelOutline,
  implicitHydrogenPosition,
  type LabelHull
} from '../chemistry/labelOutline.ts';
import {
  terminalDoubleBondCenters,
  createRingAwareBondSegments,
  createBondSegments,
  bondLineStrokeScale,
  evenParallelSegments,
  type BondSegment
} from './chemicalGeometry.ts';
import { arrowheadDefs, arrowheadMarkerSpec } from './arrowheadMarkers.ts';
import { reactionArrowPrimitives } from './reactionArrowGeometry.ts';
import {
  ANNOTATION_DASH,
  atomEndGaps,
  curvedArrowPrimitives,
  SHADOW_COLOR,
  SHADOW_OFFSET_PT,
  SHADOW_OPACITY,
  starPoints,
  type AnnotationPoint
} from './annotationGeometry.ts';
import { renderableAnnotations, type RenderableAnnotation } from '../domain/annotationRendering.ts';
import { estimateTextWidth, layoutText, type TextMeasure } from './textLayout.ts';
import { exportFontMeasure, outlineLabel, outlinePath, type ExportFonts } from './textOutlines.ts';
import { primitivePoints } from './annotationExtents.ts';
import { centroidOf } from '../domain/haptoCentroid.ts';
import { wavyBondPath } from './bondMarks.ts';

/**
 * Every atom's exported position, computed by walking the graph outward from each connected
 * component's first atom rather than standardizing each bond in isolation: a non-overridden bond
 * is redrawn at the standard length along its original direction *from the neighbor's already-
 * placed position*, and an overridden bond keeps its true drawn offset. Standardizing bonds
 * independently (anchored at each bond's own untouched original endpoints) breaks apart any chain
 * of two or more non-standard-length bonds, since the second bond starts from where the atom
 * *originally* was instead of where the walk actually placed it after redrawing the first bond -
 * exactly the layout that comes out of RDKit import or any document authored before per-bond
 * length overrides existed. A ring-closing bond (both endpoints already visited via another path)
 * is left connecting wherever the rest of the walk put its endpoints, so it may land off the
 * standard length - unavoidable once every other edge in the ring has already fixed both
 * endpoints' positions.
 */
function computeExportPositions(
  document: DrawingDocument,
  originalPositions: ReadonlyMap<string, PixelPoint>,
  overriddenBondIds: ReadonlySet<string>
): Map<string, PixelPoint> {
  const neighborsByNode = new Map<string, { neighborId: string; edgeId: string }[]>();
  const addNeighbor = (from: string, neighborId: string, edgeId: string) => {
    const neighbors = neighborsByNode.get(from);
    if (neighbors) neighbors.push({ neighborId, edgeId });
    else neighborsByNode.set(from, [{ neighborId, edgeId }]);
  };
  for (const edge of document.structure.graph.edges) {
    if (!originalPositions.has(edge.source) || !originalPositions.has(edge.target)) continue;
    // ADR 0026: a hapto bond or an agostic interaction holds no atom at a bond's length -- the
    // walk leaves it out.
    const type = document.structure.bonds[edge.id]?.type;
    if (type === 'hapto' || type === 'agostic') continue;
    addNeighbor(edge.source, edge.target, edge.id);
    addNeighbor(edge.target, edge.source, edge.id);
  }
  const exportPositions = new Map<string, PixelPoint>();
  for (const node of document.structure.graph.nodes) {
    const original = originalPositions.get(node.id);
    if (!original || exportPositions.has(node.id)) continue;
    exportPositions.set(node.id, original);
    const queue = [node.id];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      const current = exportPositions.get(currentId)!;
      const currentOriginal = originalPositions.get(currentId)!;
      for (const { neighborId, edgeId } of neighborsByNode.get(currentId) ?? []) {
        if (exportPositions.has(neighborId)) continue;
        const neighborOriginal = originalPositions.get(neighborId)!;
        const dx = neighborOriginal.x - currentOriginal.x;
        const dy = neighborOriginal.y - currentOriginal.y;
        if (overriddenBondIds.has(edgeId)) {
          exportPositions.set(neighborId, { x: current.x + dx, y: current.y + dy });
        } else {
          const length = distanceBetween(currentOriginal, neighborOriginal) || 1;
          exportPositions.set(neighborId, {
            x: current.x + (dx / length) * DEFAULT_BOND_DRAWING_CONFIG.lengthPx,
            y: current.y + (dy / length) * DEFAULT_BOND_DRAWING_CONFIG.lengthPx
          });
        }
        queue.push(neighborId);
      }
    }
  }
  return exportPositions;
}

// #427 -- one canvas annotation (reaction/scheme arrow, mechanism/curved arrow, decorative
// shape) as an SVG fragment. `toExport` maps a normalized surface point into the export's
// re-fit pixel space; the drawn shape then matches PaperVectorLayer's live rendering exactly
// (shared geometry: reactionArrowGeometry / annotationGeometry).
function annotationToSvg(
  annotation: RenderableAnnotation,
  toExport: (point: { readonly x: number; readonly y: number }) => PixelPoint,
  baseColor: string,
  defaultStrokeWidth: number,
  textFonts?: ExportFonts
): string {
  // #859 -- an authored width (pt) and opacity wrap the object's own markup, so it exports
  // exactly as drawn; without them the shared export width applies, as before.
  const lineWidth = annotation.kind === 'text' ? undefined : annotation.lineWidth;
  const strokeWidth = lineWidth !== undefined ? pointsToUnits(lineWidth) : defaultStrokeWidth;
  const attributes = [
    lineWidth !== undefined ? ` stroke-width="${roundToTwoDecimals(strokeWidth)}"` : '',
    annotation.opacity !== undefined ? ` opacity="${roundToTwoDecimals(annotation.opacity)}"` : ''
  ].join('');
  const markup = annotationMarkup(annotation, toExport, baseColor, strokeWidth, textFonts);
  return attributes && markup ? `<g${attributes}>${markup}</g>` : markup;
}

function annotationMarkup(
  annotation: RenderableAnnotation,
  toExport: (point: { readonly x: number; readonly y: number }) => PixelPoint,
  baseColor: string,
  strokeWidth: number,
  textFonts?: ExportFonts
): string {
  // #536/#756: the colour is a free-form schema string and this markup reaches thumbnails via
  // innerHTML, so it is attribute-escaped like any other authored value.
  const stroke = escapeXmlAttribute(annotation.color ?? baseColor);
  if (annotation.kind === 'text') {
    return textFonts
      ? outlinedTextToSvg(annotation, toExport, stroke, textFonts)
      : textToSvg(annotation, toExport, stroke);
  }
  if (annotation.kind === 'shape' && annotation.callout && annotation.points[0] && annotation.points[1]) {
    const a = toExport(annotation.points[0]),
      b = toExport(annotation.points[1]);
    const bubble = annotation.callout;
    const { origin, paragraphs } = calloutTextLayout(
      bubble.paragraphs,
      a,
      b,
      textFonts ? exportFontMeasure(textFonts, estimateTextWidth) : estimateTextWidth,
      bubble.variant
    );
    const text: Extract<RenderableAnnotation, { kind: 'text' }> = {
      id: annotation.id,
      kind: 'text',
      position: origin,
      paragraphs
    };
    const literal = (point: { x: number; y: number }) => point;
    return (
      `<path d="${calloutOutline(a, b, toExport(bubble.tip), bubble.variant)}" fill="${escapeXmlAttribute(bubble.variant === 'leader' ? 'none' : (annotation.fill ?? 'white'))}" stroke="${stroke}" />` +
      (textFonts ? outlinedTextToSvg(text, literal, stroke, textFonts) : textToSvg(text, literal, stroke))
    );
  }
  const dash = ANNOTATION_DASH[annotation.lineStyle ?? 'solid'];
  const dashAttr = (dashed: boolean) => {
    const value = dashed ? (dash ?? '6 4') : dash;
    return value ? ` stroke-dasharray="${value}"` : '';
  };
  const pts = (points: readonly AnnotationPoint[]) =>
    points.map((point) => `${roundToOneDecimal(point.x)},${roundToOneDecimal(point.y)}`).join(' ');

  if (annotation.kind === 'reaction-arrow') {
    const primitives = reactionArrowPrimitives(
      toExport(annotation.from),
      toExport(annotation.to),
      annotation.arrowKind,
      annotation.overlay,
      strokeWidth
    );
    return primitives
      .map((primitive) => {
        if (primitive.t === 'polygon') {
          return `<polygon points="${pts(primitive.points)}" fill="${primitive.filled ? stroke : 'none'}" stroke="${primitive.head ? 'none' : stroke}" />`;
        }
        if (primitive.t === 'path') {
          return `<path d="${primitive.d}" fill="none" stroke="${stroke}" stroke-linecap="butt"${dashAttr(primitive.dashed ?? false)} />`;
        }
        return `<polyline points="${pts(primitive.points)}" fill="none" stroke="${stroke}" stroke-linecap="butt"${dashAttr(primitive.dashed ?? false)} />`;
      })
      .join('\n    ');
  }

  if (annotation.kind === 'curved-arrow') {
    const primitives = curvedArrowPrimitives(
      toExport(annotation.from),
      toExport(annotation.to),
      annotation.head,
      annotation.bow,
      strokeWidth,
      1,
      annotation.controls?.map(toExport) as [AnnotationPoint, AnnotationPoint] | undefined,
      atomEndGaps(annotation.fromAnchor, annotation.toAnchor)
    );
    return primitives
      .map((primitive) => {
        if (primitive.t === 'path')
          return `<path d="${primitive.d}" fill="none" stroke="${stroke}" stroke-linecap="butt"${dashAttr(false)} />`;
        if (primitive.t === 'polygon')
          return `<polygon points="${pts(primitive.points)}" fill="${stroke}" stroke="none" />`;
        return `<polyline points="${pts(primitive.points)}" fill="none" stroke="${stroke}" />`;
      })
      .join('\n    ');
  }

  // shape. #866 -- the shadow is the same shape drawn first, offset down-right in grey (crisp: the
  // PDF exporter has no SVG filters), exactly as the paper draws it.
  const points = annotation.points.map(toExport);
  const offset = roundToTwoDecimals(pointsToUnits(SHADOW_OFFSET_PT));
  const shadowAttrs = (filled: boolean) =>
    `fill="${filled ? SHADOW_COLOR : 'none'}" stroke="${annotation.outline === false ? 'none' : SHADOW_COLOR}" opacity="${SHADOW_OPACITY}"`;
  const withShadow = (draw: (attrs: string, transform: string) => string, filled: boolean, transform = '') =>
    annotation.shadow
      ? `${draw(shadowAttrs(filled), `translate(${offset} ${offset})${transform ? ` ${transform}` : ''}`)}${draw('', transform)}`
      : draw('', transform);
  if (annotation.shape === 'path') {
    return withShadow(
      (shadow, transform) =>
        `<path d="${pathData({ ...annotation, points, ...(annotation.pathControls ? { pathControls: mapPathControls(annotation.pathControls, toExport) } : {}) })}" ${shadow || `fill="none" stroke="${stroke}"`} stroke-linecap="butt"${dashAttr(false)}${transform ? ` transform="${transform}"` : ''} />`,
      false
    );
  }
  const [topLeft, bottomRight] = points;
  if (!topLeft || !bottomRight) return '';
  const minX = roundToOneDecimal(Math.min(topLeft.x, bottomRight.x));
  const minY = roundToOneDecimal(Math.min(topLeft.y, bottomRight.y));
  const maxX = roundToOneDecimal(Math.max(topLeft.x, bottomRight.x));
  const maxY = roundToOneDecimal(Math.max(topLeft.y, bottomRight.y));
  const shapeStroke = annotation.outline === false ? 'none' : stroke;
  const fill = annotation.fill !== undefined ? escapeXmlAttribute(annotation.fill) : 'none';
  const filled = annotation.fill !== undefined && annotation.fill !== 'none';
  const rotate = `rotate(${annotation.rotation ?? 0} ${(minX + maxX) / 2} ${(minY + maxY) / 2})`;
  const common = (shadow: string, transform: string) =>
    `${shadow || `fill="${fill}" stroke="${shapeStroke}"`}${dashAttr(false)} transform="${transform}"`;
  if (annotation.shape === 'ellipse') {
    return withShadow(
      (shadow, transform) =>
        `<ellipse ${common(shadow, transform)} cx="${(minX + maxX) / 2}" cy="${(minY + maxY) / 2}" rx="${(maxX - minX) / 2}" ry="${(maxY - minY) / 2}" />`,
      filled,
      rotate
    );
  }
  if (annotation.shape === 'rect') {
    // #866 -- rounded corners: the radius in points, never more than half the short side.
    const radius = roundToTwoDecimals(
      Math.min(pointsToUnits(annotation.cornerRadius ?? 0), (maxX - minX) / 2, (maxY - minY) / 2)
    );
    const corners = radius > 0 ? ` rx="${radius}" ry="${radius}"` : '';
    return withShadow(
      (shadow, transform) =>
        `<rect ${common(shadow, transform)} x="${minX}" y="${minY}" width="${maxX - minX}" height="${maxY - minY}"${corners} />`,
      filled,
      rotate
    );
  }
  if (annotation.shape === 'triangle') {
    return withShadow(
      (shadow, transform) =>
        `<polygon ${common(shadow, transform)} points="${(minX + maxX) / 2},${minY} ${minX},${maxY} ${maxX},${maxY}" />`,
      filled,
      rotate
    );
  }
  return withShadow(
    (shadow, transform) =>
      `<polygon ${common(shadow, transform)} points="${pts(starPoints(minX, minY, maxX, maxY))}" />`,
    filled,
    rotate
  );
}

export interface SvgExportBondAnnotations {
  readonly bondOrders: Readonly<Record<string, BondOrder>>;
  readonly aromaticBondIds: ReadonlySet<string>;
  readonly stereoBonds: Readonly<Record<string, BondStereoValue>>;
  readonly overriddenBondIds: ReadonlySet<string>;
}

const roundToTwoDecimals = (value: number) => Math.round(value * 100) / 100;

interface Extents {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

// #757 -- the complete visual extent of everything drawn: label boxes (with charge/radical
// marks), bond strokes and wedges, arrows with their heads, shapes and text blocks.
function visualExtents(input: {
  readonly positions: ReadonlyMap<string, PixelPoint>;
  readonly labelBoxes: readonly AtomLabelBox[];
  readonly chargedAtoms: readonly { readonly id: string }[];
  readonly chargeAllowance: number;
  readonly strokeAllowance: number;
  readonly annotations: readonly RenderableAnnotation[];
  readonly toExport: (point: { readonly x: number; readonly y: number }) => PixelPoint;
  readonly strokeWidth: number;
  readonly arrowheadAllowance: number;
  readonly extraPoints: readonly PixelPoint[];
  readonly measure: TextMeasure;
}): Extents {
  const box: Extents = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
  const add = (x: number, y: number, margin = 0) => {
    box.minX = Math.min(box.minX, x - margin);
    box.minY = Math.min(box.minY, y - margin);
    box.maxX = Math.max(box.maxX, x + margin);
    box.maxY = Math.max(box.maxY, y + margin);
  };
  for (const point of input.positions.values()) add(point.x, point.y, input.strokeAllowance);
  for (const label of input.labelBoxes) {
    add(label.maskX, label.maskY);
    add(label.maskX + label.maskWidth, label.maskY + label.maskHeight);
  }
  for (const atom of input.chargedAtoms) {
    const at = input.positions.get(atom.id);
    if (at) add(at.x, at.y, input.chargeAllowance * 1.6);
  }
  const half = input.strokeWidth / 2;
  for (const annotation of input.annotations) {
    if (annotation.kind === 'text') {
      const origin = input.toExport(annotation.position);
      const layout = layoutText(annotation.paragraphs, origin, input.measure);
      add(origin.x, origin.y);
      add(origin.x + layout.width, origin.y + layout.height);
      continue;
    }
    if (annotation.kind === 'shape') {
      for (const point of [
        ...annotation.points,
        ...(annotation.pathControls ?? []).flatMap((c) => [c.incoming, c.outgoing].filter((p) => p !== undefined))
      ]) {
        const p = input.toExport(point);
        add(p.x, p.y, half);
      }
      continue;
    }
    const from = input.toExport(annotation.from);
    const to = input.toExport(annotation.to);
    const primitives =
      annotation.kind === 'reaction-arrow'
        ? reactionArrowPrimitives(from, to, annotation.arrowKind, annotation.overlay, input.strokeWidth)
        : curvedArrowPrimitives(
            from,
            to,
            annotation.head,
            annotation.bow,
            input.strokeWidth,
            1,
            annotation.controls?.map(input.toExport) as [AnnotationPoint, AnnotationPoint] | undefined,
            atomEndGaps(annotation.fromAnchor, annotation.toAnchor)
          );
    for (const primitive of primitives) for (const p of primitivePoints(primitive)) add(p.x, p.y, half);
    add(to.x, to.y, input.arrowheadAllowance);
    add(from.x, from.y, input.arrowheadAllowance);
  }
  for (const point of input.extraPoints) add(point.x, point.y, half);
  return Number.isFinite(box.minX) ? box : { minX: 0, minY: 0, maxX: 0, maxY: 0 };
}

function escapeXmlText(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeXmlAttribute(value: string): string {
  return escapeXmlText(value).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// #756 -- standalone rich text: one <text> per paragraph, one <tspan> per run. Run text and font
// names are authored strings, so both are escaped; spaces are preserved verbatim.
// #757 -- outlined text: each run becomes a filled path from the bundled export fonts, placed by
// those fonts' own advances so alignment and bounds agree with the outlines. A run with a
// character the fonts lack stays editable <text> at the same position (reported before export).
function outlinedTextToSvg(
  annotation: Extract<RenderableAnnotation, { kind: 'text' }>,
  toExport: (point: { readonly x: number; readonly y: number }) => PixelPoint,
  fill: string,
  fonts: ExportFonts
): string {
  const measure = exportFontMeasure(fonts, estimateTextWidth);
  const parts = layoutText(annotation.paragraphs, toExport(annotation.position), measure).lines.flatMap((line) => {
    const width = line.spans.reduce((sum, span) => sum + measure(span.text, span), 0);
    let cursor = line.anchor === 'start' ? line.x : line.anchor === 'middle' ? line.x - width / 2 : line.x - width;
    return line.spans.flatMap((span) => {
      const x = cursor;
      cursor += measure(span.text, span);
      const path = outlinePath(fonts, span.text, span, x, span.y);
      if (path !== null) return path ? [`<path d="${path}" />`] : [];
      return [
        `<text x="${roundToOneDecimal(x)}" y="${roundToOneDecimal(span.y)}" font-family="${escapeXmlAttribute(span.fontFamily)}" font-size="${roundToOneDecimal(span.fontSizePx)}"${span.bold ? ' font-weight="700"' : ''}${span.italic ? ' font-style="italic"' : ''} xml:space="preserve" style="white-space:pre">${escapeXmlText(span.text)}</text>`
      ];
    });
  });
  return parts.length ? `<g fill="${fill}" stroke="none">${parts.join('')}</g>` : '';
}

function textToSvg(
  annotation: Extract<RenderableAnnotation, { kind: 'text' }>,
  toExport: (point: { readonly x: number; readonly y: number }) => PixelPoint,
  fill: string
): string {
  return layoutText(annotation.paragraphs, toExport(annotation.position))
    .lines.filter((line) => line.spans.length > 0)
    .map((line) => {
      const spans = line.spans
        .map(
          (span) =>
            `<tspan${span.dy !== 0 ? ` dy="${roundToOneDecimal(span.dy)}"` : ''} font-family="${escapeXmlAttribute(span.fontFamily)}" font-size="${roundToOneDecimal(span.fontSizePx)}"${span.bold ? ' font-weight="700"' : ''}${span.italic ? ' font-style="italic"' : ''}>${escapeXmlText(span.text)}</tspan>`
        )
        .join('');
      return `<text x="${roundToOneDecimal(line.x)}" y="${roundToOneDecimal(line.baseline)}" text-anchor="${line.anchor}" fill="${fill}" stroke="none" font-weight="400" xml:space="preserve" style="white-space:pre">${spans}</text>`;
    })
    .join('\n    ');
}

// #757 -- how an export differs from a thumbnail render. With no options the output is the
// historical thumbnail rendering (bonds normalized to the standard length, fixed 40px margin).
export interface SvgRenderOptions {
  // 'authored' keeps the drawn geometry (WYSIWYG export); 'normalized' redraws every
  // non-overridden bond at the standard length (thumbnails, the default).
  readonly geometry?: 'normalized' | 'authored';
  // Selection scope: only these atoms (with the bonds between them) and annotations are drawn.
  // Annotation anchors still resolve against the whole drawing, so a selected arrow stays where it
  // is without pulling in its target.
  readonly include?: { readonly atomIds: ReadonlySet<string>; readonly annotationIds: ReadonlySet<string> };
  // #927 -- atoms and bonds to mark (a search match): a soft halo drawn beneath the structure, so
  // the drawing itself is unchanged and stays readable. The colour defaults to MATCH_HIGHLIGHT_COLOR.
  readonly highlight?: {
    readonly atomIds: ReadonlySet<string>;
    readonly bondIds: ReadonlySet<string>;
    readonly color?: string;
  };
  // #780 -- draw each stereocentre's R/S and each defined double bond's E/Z, as the paper shows
  // them with Show R/S Labels on (owner, 2026-11-02: exported when shown on the paper).
  readonly stereoLabels?: boolean;
  // When set, the SVG is cropped to the content's complete visual extents (labels, strokes,
  // arrowheads, text) plus this padding in px, instead of the fixed margin.
  readonly paddingPx?: number;
  // Emit width/height in millimetres (96 px per inch) so the file has its intended physical size.
  readonly physicalSize?: boolean;
  // Loaded export fonts (textOutlines.ts): text is drawn as outlines; without them, editable text.
  readonly textOutlines?: ExportFonts;
  // #758 -- the export background: a CSS colour paints the whole image and the atom-label masks;
  // null is transparent (no backdrop, no label masks -- bonds are already trimmed at labels).
  // Absent keeps the thumbnail look (white label masks, no backdrop).
  readonly background?: string | null;
  // #758 -- the chosen output size in mm; artwork scales uniformly to fit (proportions kept).
  readonly sizeMm?: { readonly width: number; readonly height: number };
  // #772 -- Page scope: the image is exactly the page, (0,0) to (width,height) in drawing units, at
  // its physical size; anything beyond it is clipped by the frame (accepted, shown in the preview).
  // #805: the grown page may start left of or above the origin.
  readonly pageBounds?: { readonly x?: number; readonly y?: number; readonly width: number; readonly height: number };
  // #1141 (ADR 0025) -- the "+" the paper draws between a reaction's reactants and between its
  // products (#739), in drawing units, so a picture of a reaction looks as the paper does.
  readonly plusSigns?: readonly { readonly x: number; readonly y: number }[];
}

export const MM_PER_PX = 25.4 / 96;

/** #927 -- the match halo: amber, distinct from the black structure and red/blue heteroatom labels,
 *  and drawn on the white paper every structure sits on (so it doesn't follow the app's theme). */
export const MATCH_HIGHLIGHT_COLOR = '#f5a623';

export function drawingDocumentToSvg(
  document: DrawingDocument,
  bonds: SvgExportBondAnnotations,
  color: string,
  paperSize: PaperSize,
  // #429 -- namespaces this SVG's `<marker>` ids. Pass a stable per-instance value when the
  // output is inlined into the app DOM (a thumbnail), so its arrowhead markers can't collide
  // with the live canvas's or another thumbnail's. Empty (the default) keeps bare ids for the
  // standalone "Export SVG" download.
  markerIdPrefix = '',
  // #648 -- text drawn in place of an atom's element symbol, by node id (Markush R labels), so an
  // exported Markush scaffold shows `R1` rather than the carbon standing in for it.
  atomLabelOverrides: Readonly<Record<string, string>> = {},
  // #650 -- Markush attachment points: a line from each label atom to its ring's centre, drawn in
  // the export's own (re-walked) positions so it stays attached to the ring as exported.
  attachmentLines: readonly { readonly atomId: string; readonly ringAtomIds: readonly string[] }[] = [],
  // #656 -- Markush repeat units: brackets and count labels, placed from the export's positions.
  repeatUnits: readonly RepeatUnitRef[] = [],
  options: SvgRenderOptions = {}
): string {
  const authored = options.geometry === 'authored';
  const cropped = options.paddingPx !== undefined || options.pageBounds !== undefined;
  const { bondOrders, aromaticBondIds, stereoBonds, overriddenBondIds } = bonds;
  const width = roundToOneDecimal(paperSize.width);
  const height = roundToOneDecimal(paperSize.height);
  const size = { width, height };
  // #736 step 2 -- a contracted group is drawn as one label at its attachment atom.
  const contracted = contractedDisplay(document);
  const allLines = documentToLines(document).filter((line) => !contracted.hiddenEdgeIds.has(line.id));
  const allAtoms = withAtomLabelOverrides(
    documentToAtoms(document).filter((atom) => !contracted.hiddenAtomIds.has(atom.id)),
    { ...contracted.labels, ...atomLabelOverrides }
  );
  const edgeById = new Map(document.structure.graph.edges.map((edge) => [edge.id, edge]));
  const include = options.include;
  const atoms = include ? allAtoms.filter((atom) => include.atomIds.has(atom.id)) : allAtoms;
  const lines = include
    ? allLines.filter((line) => {
        const edge = edgeById.get(line.id);
        return !!edge && include.atomIds.has(edge.source) && include.atomIds.has(edge.target);
      })
    : allLines;
  const originalPositions = new Map(atoms.map((atom) => [atom.id, normalizedToPixel(atom.position, size)]));
  const walkedPositions = authored
    ? originalPositions
    : computeExportPositions(document, originalPositions, overriddenBondIds);
  // The graph walk above redraws each non-overridden bond at the standard length regardless of
  // its original length, which can push the walked layout's extent well outside the source
  // document's own coordinate space - e.g. a document whose bonds were mostly shorter than
  // standard grows a little at every step of every chain, and that compounds across a long chain
  // far past the original canvas size. Re-fit the SVG's own dimensions (and every position in it)
  // to the walked layout's actual bounding box - with a margin for labels/wedges, which extend a
  // little past their atom's exact point - instead of reusing the source document's box, or
  // content past the original box's edges would be clipped by the viewBox.
  // #427 -- canvas annotations (reaction/curved arrows, shapes) projected to the pre-offset pixel
  // space, so they land in the re-fit viewBox next to the molecule and count toward its bounds
  // (an arrow drawn away from the structure would otherwise be clipped).
  const renderables = renderableAnnotations(document.annotations ?? [], allAtoms, allLines, size, size, false).filter(
    (annotation) => !include || include.annotationIds.has(annotation.id)
  );
  const annotationAnchorPoints = renderables.flatMap((annotation) => {
    if (annotation.kind === 'text') {
      const origin = normalizedToPixel(annotation.position, size);
      const { width: textWidth, height: textHeight } = layoutText(annotation.paragraphs, origin);
      return [origin, { x: origin.x + textWidth, y: origin.y + textHeight }];
    }
    return (
      annotation.kind === 'shape'
        ? shapeExtentPoints(annotation, size)
        : [annotation.from, annotation.to, ...(annotation.kind === 'curved-arrow' ? (annotation.controls ?? []) : [])]
    ).map((point) => normalizedToPixel(point, size));
  });

  const walkedPoints = [...walkedPositions.values(), ...annotationAnchorPoints];
  const EXPORT_MARGIN_PX = 40;
  const bounds =
    walkedPoints.length > 0
      ? walkedPoints.reduce(
          (acc, point) => ({
            minX: Math.min(acc.minX, point.x),
            minY: Math.min(acc.minY, point.y),
            maxX: Math.max(acc.maxX, point.x),
            maxY: Math.max(acc.maxY, point.y)
          }),
          { minX: walkedPoints[0]!.x, minY: walkedPoints[0]!.y, maxX: walkedPoints[0]!.x, maxY: walkedPoints[0]!.y }
        )
      : { minX: 0, minY: 0, maxX: width, maxY: height };
  // Cropped exports keep drawing coordinates and move the viewBox instead (see the end).
  const offsetX = cropped ? 0 : EXPORT_MARGIN_PX - bounds.minX;
  const offsetY = cropped ? 0 : EXPORT_MARGIN_PX - bounds.minY;
  const exportPositions = new Map(
    [...walkedPositions].map(([nodeId, point]) => [nodeId, { x: point.x + offsetX, y: point.y + offsetY }])
  );
  // ADR 0026 D2: a bond's far end -- for a hapto bond, the middle of its ligand atoms.
  const endOf = (edge: { readonly id: string; readonly target: string }) => {
    const ligand = document.structure.bonds[edge.id]?.ligandAtomIds;
    return ligand ? centroidOf(ligand, (id) => exportPositions.get(id)) : exportPositions.get(edge.target);
  };
  // ADR 0026 D6: a bond's near end -- for an agostic interaction, the middle of its C–H bond.
  const startOf = (edge: { readonly id: string; readonly source: string }) => {
    if (document.structure.bonds[edge.id]?.type !== 'agostic') return exportPositions.get(edge.source);
    const carbonEdge = document.structure.graph.edges.find(
      (other) =>
        other.id !== edge.id &&
        (other.source === edge.source || other.target === edge.source) &&
        isChemicalBond(document.structure.bonds[other.id]) &&
        document.structure.atoms[other.source === edge.source ? other.target : other.source]?.atomicNumber === 6
    );
    const carbon = carbonEdge
      ? carbonEdge.source === edge.source
        ? carbonEdge.target
        : carbonEdge.source
      : edge.source;
    return centroidOf([edge.source, carbon], (id) => exportPositions.get(id));
  };
  const exportWidth = roundToOneDecimal(bounds.maxX - bounds.minX + EXPORT_MARGIN_PX * 2);
  const exportHeight = roundToOneDecimal(bounds.maxY - bounds.minY + EXPORT_MARGIN_PX * 2);
  // Large molecules get their whole 2D layout scaled down to fit a fixed-size canvas (see
  // fittedCoordinates), which shrinks bond length arbitrarily. Everything drawn on top of a bond -
  // labels, wedges, double-bond gaps - is otherwise a fixed pixel size, so without this it
  // overlaps into an illegible mess once bonds get small enough. Scaling those fixed sizes down
  // together with the bonds keeps the depiction proportionally readable instead of overlapping,
  // down to a per-property floor so it never shrinks all the way to invisible.
  //
  // Every non-overridden bond is now redrawn at exactly DEFAULT_BOND_DRAWING_CONFIG.lengthPx, so
  // the typical length among them is trivially that constant - no median needed. Only fall back to
  // computing one (via #80's original formula) in the rare case every bond is overridden.
  // Authored geometry keeps every bond's drawn length, so the typical length is measured, as the
  // live paper does.
  const nonOverriddenCount = lines.length - overriddenBondIds.size;
  const typicalLength =
    !authored && nonOverriddenCount > 0
      ? DEFAULT_BOND_DRAWING_CONFIG.lengthPx
      : lines.length > 0
        ? typicalBondLengthPx(lines, size)
        : DEFAULT_BOND_DRAWING_CONFIG.lengthPx;
  const labelScale = typicalLength > 0 ? Math.min(1, typicalLength / DEFAULT_BOND_DRAWING_CONFIG.lengthPx) : 1;
  // Bond stroke width follows a step function of the document's typical rendered bond length
  // (see boundedBondStrokeWidthPx), not the same proportional labelScale used for labels/wedges -
  // a standard 45px bond always resolves to 3px, matching the app's prior fixed default.
  const bondStrokeWidth = boundedBondStrokeWidthPx(typicalLength);
  const wedgeHalfWidth = Math.max(1.5, 5 * labelScale);
  const labelFontSize = Math.max(8, roundToOneDecimal(17 * labelScale));
  // #977: an atom's H label beside its symbol as on the live canvas, at the drawn (rounded) position.
  const hydrogenAt = (atom: (typeof atoms)[number], x: number, y: number) => {
    if (!atom.implicitHydrogenDirection) return undefined;
    const at = implicitHydrogenPosition(atom, x, y, atom.implicitHydrogenDirection, 17 * labelScale);
    return { x: roundToOneDecimal(at.x), y: roundToOneDecimal(at.y) };
  };

  // #965: every label's letter shapes, by node id, so a bond/wedge/dative head stops a fixed gap
  // short of the letters (mirrors usePaperGeometry for the live canvas). Placed at the rounded
  // position the label itself is drawn at.
  const labelHullsByNode = new Map<string, LabelHull[]>();
  // The label boxes still size the export's extents.
  const labelBoxes: AtomLabelBox[] = [];
  for (const atom of atoms) {
    if (!atomShowsLabel(atom)) continue;
    const position = exportPositions.get(atom.id);
    if (!position) continue;
    const x = roundToOneDecimal(position.x);
    const y = roundToOneDecimal(position.y);
    const hydrogen = hydrogenAt(atom, x, y);
    labelHullsByNode.set(atom.id, atomLabelHulls(atom, x, y, hydrogen, labelFontSize));
    labelBoxes.push(
      atomLabelBox(
        atom.atomicNumber,
        atom.isotope,
        x,
        y,
        hydrogen?.x,
        hydrogen?.y,
        atom.implicitHydrogenCount,
        labelScale,
        undefined,
        atom.labelOverride
      )
    );
  }
  const chargePositions = placeChargeLabels(
    atoms.flatMap((atom) => {
      const point = exportPositions.get(atom.id);
      if (!point) return [];
      return [
        {
          id: atom.id,
          x: point.x,
          y: point.y,
          text:
            atom.resonanceCharge !== undefined
              ? resonanceChargeLabel(atom.resonanceCharge)
              : formalChargeLabel(atom.formalCharge),
          radical: radicalLabel(atom.radicalElectronCount),
          offsetX: formalChargeLabelOffsetX(atom.atomicNumber),
          hulls: labelHullsByNode.get(atom.id) ?? []
        }
      ];
    }),
    lines.flatMap((line) => {
      const edge = edgeById.get(line.id),
        from = edge && exportPositions.get(edge.source),
        to = edge && endOf(edge);
      if (!from || !to) return [];
      return [
        {
          from,
          to,
          radius:
            stereoBonds[line.id]?.type === 'wedge'
              ? wedgeHalfWidth
              : (document.structure.bonds[line.id]?.order ?? 1) > 1
                ? 4 * labelScale
                : bondStrokeWidth / 2
        }
      ];
    }),
    labelScale,
    Math.max(8, roundToOneDecimal(15 * labelScale))
  );
  for (const charge of chargePositions.values()) labelBoxes.push(charge.box);
  // #780 -- R/S beside stereocentres, then E/Z beside double bonds, clear of every label so far.
  const stereoLabelSpots: { readonly text: string; readonly x: number; readonly y: number }[] = [];
  const stereoFontSize = roundToOneDecimal(12 * labelScale);
  if (options.stereoLabels) {
    const cip = document.derivedPropertySets.find((set) => set.kind === 'cip');
    const atomDescriptors = cip?.kind === 'cip' ? cip.atomDescriptors : {};
    const bondDescriptors = cip?.kind === 'cip' ? cip.bondDescriptors : {};
    const stereoBondsForLabels = lines.flatMap((line) => {
      const edge = edgeById.get(line.id),
        from = edge && exportPositions.get(edge.source),
        to = edge && endOf(edge);
      if (!from || !to) return [];
      return [
        {
          id: line.id,
          from,
          to,
          radius: (document.structure.bonds[line.id]?.order ?? 1) > 1 ? 4 * labelScale : bondStrokeWidth / 2
        }
      ];
    });
    const atomPlacements = placeStereoLabels(
      atoms.flatMap((atom) => {
        const point = exportPositions.get(atom.id);
        return point
          ? [{ id: atom.id, x: point.x, y: point.y, text: '', offsetX: 0, hulls: labelHullsByNode.get(atom.id) ?? [] }]
          : [];
      }),
      stereoBondsForLabels,
      labelScale,
      labelBoxes,
      atomDescriptors
    );
    for (const [id, placed] of atomPlacements) {
      stereoLabelSpots.push({ text: `(${atomDescriptors[id]})`, x: placed.x, y: placed.y });
      labelBoxes.push(placed.box);
    }
    const bondPlacements = placeBondStereoLabels(
      stereoBondsForLabels
        .filter((bond) => bondDescriptors[bond.id])
        .map((bond) => ({ id: bond.id, from: bond.from, to: bond.to, text: `(${bondDescriptors[bond.id]})` })),
      stereoBondsForLabels,
      labelScale,
      labelBoxes
    );
    for (const [id, placed] of bondPlacements) {
      stereoLabelSpots.push({ text: `(${bondDescriptors[id]})`, x: placed.x, y: placed.y });
      labelBoxes.push(placed.box);
    }
  }
  const labelGap = LABEL_BOND_GAP_PX * labelScale;
  const bondClearance = labelGap + bondStrokeWidth / 2;
  const trimAxisToLabels = (
    sourceId: string,
    targetId: string,
    start: PixelPoint,
    end: PixelPoint,
    endClearance = bondClearance
  ): readonly [PixelPoint, PixelPoint] => {
    const sourceHulls = labelHullsByNode.get(sourceId);
    const targetHulls = labelHullsByNode.get(targetId);
    const trimmedStart = sourceHulls ? clipToLabelOutline(start, end, sourceHulls, bondClearance) : start;
    const trimmedEnd = targetHulls ? clipToLabelOutline(end, start, targetHulls, endClearance) : end;
    const axisX = end.x - start.x;
    const axisY = end.y - start.y;
    if ((trimmedEnd.x - trimmedStart.x) * axisX + (trimmedEnd.y - trimmedStart.y) * axisY <= 0) {
      return [start, end];
    }
    return [trimmedStart, trimmedEnd];
  };
  // Each parallel line of a double/triple bond is also trimmed on its own (see usePaperGeometry).
  const trimSegmentToLabels = (segment: BondSegment, sourceId: string, targetId: string): BondSegment => {
    const sourceHulls = labelHullsByNode.get(sourceId);
    const targetHulls = labelHullsByNode.get(targetId);
    return {
      ...segment,
      start: sourceHulls ? clipToLabelOutline(segment.start, segment.end, sourceHulls, bondClearance) : segment.start,
      end: targetHulls ? clipToLabelOutline(segment.end, segment.start, targetHulls, bondClearance) : segment.end
    };
  };

  const aromaticCenters = new Map<string, PixelPoint>();
  for (const [edgeId, path] of Object.entries(aromaticRingAtomPaths(document))) {
    const points = path.map((nodeId) => exportPositions.get(nodeId));
    if (points.some((point) => !point)) continue;
    aromaticCenters.set(edgeId, {
      x: points.reduce((sum, point) => sum + point!.x, 0) / points.length,
      y: points.reduce((sum, point) => sum + point!.y, 0) / points.length
    });
  }
  // #422: a plain (non-aromatic) double bond in exactly one ring also gets its second line inset
  // toward that ring's interior. Skip anything the aromatic pass above already placed -- a bond
  // that's part of a fused aromatic system should keep that more specific ring path.
  const exportAtoms = atoms.flatMap((atom) => {
    const position = exportPositions.get(atom.id);
    return position ? [{ ...atom, position }] : [];
  });
  const exportEndpoints = Object.fromEntries(
    document.structure.graph.edges.map((edge) => [edge.id, [edge.source, edge.target] as const])
  );
  for (const [edgeId, center] of Object.entries(
    nonFusedRingDoubleBondCenters(exportAtoms, lines, bondOrders, exportEndpoints)
  ))
    if (!aromaticCenters.has(edgeId)) aromaticCenters.set(edgeId, center);
  const terminalCenters = terminalDoubleBondCenters(
    document.structure.graph.edges.flatMap((edge) => {
      const start = exportPositions.get(edge.source),
        end = endOf(edge);
      return start && end ? [{ id: edge.id, start, end, order: bondOrders[edge.id] ?? 1 }] : [];
    })
  );
  // #453: a fusion double bond (shared by exactly two rings) faces whichever ring the user has
  // explicitly chosen (stored as an atom private to that ring). Left unset, it stays out of this
  // map entirely so the segment-selection code below falls back to the plain symmetric double-bond
  // rendering, matching neither ring specifically -- the same neutral default as the live-editing
  // render path.
  for (const [edgeId, sides] of Object.entries(
    fusedRingDoubleBondSides(exportAtoms, lines, bondOrders, exportEndpoints)
  )) {
    if (aromaticCenters.has(edgeId)) continue;
    const chosenAtomId =
      stereoBonds[edgeId]?.type === 'double-bond-side' ? stereoBonds[edgeId].facingAtomId : undefined;
    const side = sides.find((candidate) => candidate.facingAtomId === chosenAtomId);
    if (side) aromaticCenters.set(edgeId, side.center);
  }
  // #391: for each dative bond, the point the shaft stops at (one head-length short of where the
  // arrow should touch) so the `arrowhead-full` <marker> forms the tip.
  const dativeHeadLength = arrowheadMarkerSpec('arrowhead-full').length * labelScale;
  const dativeShaftEnds = new Map<string, { end: PixelPoint; acceptorId: string }>();
  for (const line of lines) {
    const stereo = stereoBonds[line.id];
    if (stereo?.type !== 'dative') continue;
    const from = exportPositions.get(stereo.donorNodeId);
    const acceptorCentre = exportPositions.get(stereo.acceptorNodeId);
    if (!from || !acceptorCentre) continue;
    const length = Math.hypot(acceptorCentre.x - from.x, acceptorCentre.y - from.y) || 1;
    const ux = (acceptorCentre.x - from.x) / length;
    const uy = (acceptorCentre.y - from.y) / length;
    const acceptorHulls = labelHullsByNode.get(stereo.acceptorNodeId);
    const tip = acceptorHulls ? clipToLabelOutline(acceptorCentre, from, acceptorHulls, labelGap) : acceptorCentre;
    const tipGap = acceptorHulls ? 0 : dativeHeadLength * 0.9;
    dativeShaftEnds.set(line.id, {
      end: { x: tip.x - ux * (tipGap + dativeHeadLength), y: tip.y - uy * (tipGap + dativeHeadLength) },
      acceptorId: stereo.acceptorNodeId
    });
  }

  // #868 -- a bond's own colour and width (pt), an atom label's own or element colour.
  const bondAppearance = document.depiction.bondAppearance ?? {};
  const atomAppearance = document.depiction.atomAppearance ?? {};
  const bondColor = (bondId: string) => {
    const value = bondAppearance[bondId]?.color;
    return value ? escapeXmlAttribute(value) : undefined;
  };
  const bondAttrs = (bondId: string, strokeScale = 1) => {
    const look = bondAppearance[bondId];
    if (!look && strokeScale === 1) return '';
    const width = look?.lineWidth !== undefined ? pointsToUnits(look.lineWidth) : undefined;
    return `${look?.color ? ` stroke="${escapeXmlAttribute(look.color)}"` : ''}${
      width !== undefined || strokeScale !== 1
        ? ` stroke-width="${roundToTwoDecimals((width ?? bondStrokeWidth) * strokeScale)}"`
        : ''
    }`;
  };
  const labelFill = (atomId: string, atomicNumber: number) => {
    const value = atomLabelColor(atomAppearance[atomId], atomicNumber, undefined);
    return value ? ` fill="${escapeXmlAttribute(value)}"` : '';
  };
  const rendered = lines
    .flatMap((line) => {
      const edge = edgeById.get(line.id);
      const rawStart = edge && startOf(edge);
      const rawEnd = edge && endOf(edge);
      if (!edge || !rawStart || !rawEnd) return [];
      if (stereoBonds[line.id]?.type === 'wedge') return [];
      // A hapto bond ends at no atom, nor does an agostic interaction start at one, so no label
      // trims that end.
      const type = document.structure.bonds[edge.id]?.type;
      let [start, end] = trimAxisToLabels(
        type === 'agostic' ? '' : edge.source,
        type === 'hapto' ? '' : edge.target,
        rawStart,
        rawEnd
      );
      // #391: draw a dative bond donor -> (shaft end) so `marker-end` lands the head on the acceptor.
      const dative = dativeShaftEnds.get(line.id);
      if (dative) {
        start = edge.source === dative.acceptorId ? end : start;
        end = dative.end;
      }
      const ringCenter = aromaticCenters.get(line.id);
      const sideCenter = ringCenter ?? terminalCenters[line.id];
      const crossed = stereoBonds[line.id]?.type === 'crossed-double';
      const segments =
        !crossed && sideCenter
          ? createRingAwareBondSegments(
              ringCenter ? start : rawStart,
              ringCenter ? end : rawEnd,
              bondOrders[line.id] ?? 1,
              sideCenter,
              aromaticBondIds.has(line.id),
              ringCenter !== undefined || terminalCenters[line.id]!.insetStart,
              ringCenter !== undefined || terminalCenters[line.id]!.insetEnd
            )
          : // A symmetric double/triple bond is laid out from the atom centres, each line then trimmed
            // on its own, so every line keeps the same gap from the letters.
            createBondSegments(
              ...((bondOrders[line.id] ?? 1) > 1 ? ([rawStart, rawEnd] as const) : ([start, end] as const)),
              bondOrders[line.id] ?? 1,
              crossed
            );
      const eachTrimmed =
        segments.length > 1
          ? segments.map((segment) => trimSegmentToLabels(segment, edge.source, edge.target))
          : segments;
      // #1130, #1187: multi-line bonds (double through hextuple) end square, all lines the same length.
      const trimmed = segments.length > 1 ? evenParallelSegments(segments, eachTrimmed) : eachTrimmed;
      const stereoType = stereoBonds[line.id]?.type;
      const kind =
        stereoType === 'hydrogen' || stereoType === 'wavy' || stereoType === 'agostic' ? stereoType : undefined;
      const strokeScale = bondLineStrokeScale(bondOrders[line.id] ?? 1);
      return trimmed.map((segment) => ({
        ...segment,
        dativeHead: dative !== undefined,
        bondId: line.id,
        kind,
        strokeScale
      }));
    })
    .flatMap((segment) => {
      const [x1, y1, x2, y2] = [segment.start.x, segment.start.y, segment.end.x, segment.end.y].map(roundToOneDecimal);
      if (segment.kind === 'wavy')
        return [
          `<path d="${wavyBondPath(x1!, y1!, x2!, y2!, 3 * labelScale)}" fill="none"${bondAttrs(segment.bondId)} />`
        ];
      return [
        `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"${
          segment.dativeHead
            ? ` marker-end="url(#${markerIdPrefix}arrowhead-full)"`
            : segment.kind === 'agostic'
              ? ` marker-end="url(#${markerIdPrefix}arrowhead-half)"`
              : ''
        }${segment.kind === 'hydrogen' ? ' stroke-dasharray="2 5"' : ''}${bondAttrs(segment.bondId, segment.strokeScale)} />`
      ];
    })
    .join('\n    ');
  const attachmentElements = attachmentLines.flatMap(({ atomId, ringAtomIds }) => {
    const label = exportPositions.get(atomId);
    const ring = ringAtomIds.map((id) => exportPositions.get(id));
    if (!label || ring.length === 0 || ring.some((corner) => !corner)) return [];
    const centre = {
      x: ring.reduce((sum, corner) => sum + corner!.x, 0) / ring.length,
      y: ring.reduce((sum, corner) => sum + corner!.y, 0) / ring.length
    };
    const [start, end] = trimAxisToLabels(atomId, '', label, centre);
    return [
      `<line x1="${roundToOneDecimal(start.x)}" y1="${roundToOneDecimal(start.y)}" x2="${roundToOneDecimal(end.x)}" y2="${roundToOneDecimal(end.y)}" />`
    ];
  });
  const exportEdges = document.structure.graph.edges.map(({ source, target }) => ({ source, target }));
  const repeatBrackets = repeatUnits.flatMap((unit) => {
    const brackets = repeatUnitBrackets(exportPositions, exportEdges, unit);
    return brackets ? [brackets] : [];
  });
  const bracketElements = repeatBrackets.flatMap((unit) =>
    unit.strokes.map(
      (stroke) =>
        `<polyline points="${stroke.map((point) => `${roundToOneDecimal(point.x)},${roundToOneDecimal(point.y)}`).join(' ')}" />`
    )
  );
  // #792 -- with export fonts, every chemical label is a vector path (Arimo Bold) placed where its
  // live <text> would be; without them (thumbnails, the editor) or when the font lacks a character,
  // it stays <text>. `text` is raw (escaped here); `attributes` are the <text>'s own extras.
  const label = (
    text: string,
    x: number,
    y: number,
    fontSizePx: number,
    attributes = '',
    anchor: 'middle' | 'start' = 'middle'
  ) => {
    const path = options.textOutlines ? outlineLabel(options.textOutlines, text, fontSizePx, x, y, anchor) : null;
    return path !== null
      ? `<path d="${path}" />`
      : `<text x="${roundToOneDecimal(x)}" y="${roundToOneDecimal(y)}"${attributes}>${escapeXmlText(text)}</text>`;
  };
  const bracketLabels = repeatBrackets
    .map((unit) =>
      label(
        unit.label.text,
        unit.label.x,
        unit.label.y,
        12,
        ' text-anchor="start" font-size="12" font-weight="600"',
        'start'
      )
    )
    .join('\n    ');
  const bondElements = [rendered, ...attachmentElements, ...bracketElements].filter(Boolean).join('\n    ');
  const wedges = lines
    .flatMap((line) => {
      const stereo = stereoBonds[line.id];
      if (stereo?.type !== 'wedge') return [];
      const edge = edgeById.get(line.id);
      if (!edge) return [];
      const toNodeId = edge.source === stereo.fromNodeId ? edge.target : edge.source;
      const rawFrom = exportPositions.get(stereo.fromNodeId);
      const rawTo = exportPositions.get(toNodeId);
      if (!rawFrom || !rawTo) return [];
      // #387/#965: the wedge stops short of the letters, narrow point at the source symbol; its broad
      // end's corners reach wedgeHalfWidth off the axis.
      const [from, to] = trimAxisToLabels(stereo.fromNodeId, toNodeId, rawFrom, rawTo, labelGap + wedgeHalfWidth);
      const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
      const nx = -(to.y - from.y) / length;
      const ny = (to.x - from.x) / length;
      if (stereo.style === 'solid') {
        // A solid wedge is a filled trapezoid. Override the bond-lines
        // group's fill="none" stroke instead of inheriting it, or this renders as a hollow outline.
        const lineWidth = bondAppearance[line.id]?.lineWidth;
        const narrowHalfWidth = (lineWidth !== undefined ? pointsToUnits(lineWidth) : bondStrokeWidth) / 2;
        return [
          `<polygon points="${from.x - nx * narrowHalfWidth},${from.y - ny * narrowHalfWidth} ${from.x + nx * narrowHalfWidth},${from.y + ny * narrowHalfWidth} ${to.x + nx * wedgeHalfWidth},${to.y + ny * wedgeHalfWidth} ${to.x - nx * wedgeHalfWidth},${to.y - ny * wedgeHalfWidth}" fill="${bondColor(line.id) ?? color}" stroke="none" />`
        ];
      }
      return [
        [0.18, 0.34, 0.5, 0.66, 0.82, 1]
          .map((fraction) => {
            const x = from.x + (to.x - from.x) * fraction;
            const y = from.y + (to.y - from.y) * fraction;
            const half = wedgeHalfWidth * fraction;
            return `<line x1="${x - nx * half}" y1="${y - ny * half}" x2="${x + nx * half}" y2="${y + ny * half}"${bondAttrs(line.id)} />`;
          })
          .join('\n    ')
      ];
    })
    .join('\n    ');
  const labels = atoms
    .filter(atomShowsLabel)
    .flatMap((atom) => {
      const position = exportPositions.get(atom.id);
      if (!position) return [];
      const x = roundToOneDecimal(position.x);
      const y = roundToOneDecimal(position.y);
      const at = hydrogenAt(atom, x, y);
      const hydrogen = at ? label(implicitHydrogenLabel(atom.implicitHydrogenCount), at.x, at.y, labelFontSize) : '';
      const chargeOffsetX = formalChargeLabelOffsetX(atom.atomicNumber) * labelScale;
      const chargeRadicalFontSize = Math.max(8, roundToOneDecimal(15 * labelScale));
      const chargeRadicalOffsetY = roundToOneDecimal(9 * labelScale);
      const chargeLabel =
        atom.resonanceCharge !== undefined
          ? resonanceChargeLabel(atom.resonanceCharge)
          : formalChargeLabel(atom.formalCharge);
      const chargeAttributes = ` font-size="${chargeRadicalFontSize}" font-weight="800"`;
      const charge = chargeLabel
        ? label(
            chargeLabel,
            chargePositions.get(atom.id)?.x ?? x + chargeOffsetX,
            chargePositions.get(atom.id)?.y ?? y - chargeRadicalOffsetY,
            chargeRadicalFontSize,
            chargeAttributes
          )
        : '';
      const radical = radicalLabel(atom.radicalElectronCount);
      const radicalMarker = radical
        ? label(radical, x - chargeOffsetX, y - chargeRadicalOffsetY, chargeRadicalFontSize, chargeAttributes)
        : '';
      const symbol =
        atom.labelOverride !== undefined
          ? atom.labelOverride
          : isotopeSuperscript(atom.isotope) + elementSymbol(atom.atomicNumber);
      // #965: no box or halo behind the symbol -- bonds stop short of the letters above, so the
      // background (any colour, or none) shows around them.
      return [
        `<g${labelFill(atom.id, atom.atomicNumber)}>${label(symbol, x, y, labelFontSize)}${hydrogen}${charge}${radicalMarker}</g>`
      ];
    })
    .join('\n    ');

  // #427 / #875 -- foreground annotations above the molecule; background shapes below it. `renderableAnnotations`
  // gave normalized surface points; map them back to pixels and into the re-fit viewBox.
  const toExportAnnotationPoint = (point: { readonly x: number; readonly y: number }): PixelPoint => {
    const pixel = normalizedToPixel(point, size);
    return { x: pixel.x + offsetX, y: pixel.y + offsetY };
  };
  const annotationLayerSvg = (background: boolean) =>
    renderables
      .filter((annotation) => isBackgroundShape(annotation) === background)
      .map((annotation) =>
        annotationToSvg(annotation, toExportAnnotationPoint, color, bondStrokeWidth, options.textOutlines)
      )
      .filter((fragment) => fragment.length > 0)
      .join('\n    ');
  const annotationsSvg = annotationLayerSvg(false);
  const backgroundAnnotationsSvg = annotationLayerSvg(true);

  // #1141 -- the paper's "+" signs (#739), sized as the paper sizes them (22 px at the label scale).
  const plusFontSize = roundToOneDecimal(22 * labelScale);
  // Given in drawing units; the annotation mapping takes normalized points, as renderableAnnotations gives.
  const plusPoints = (options.plusSigns ?? []).map((point) =>
    toExportAnnotationPoint({
      x: point.x / document.depiction.coordinateSet.width,
      y: point.y / document.depiction.coordinateSet.height
    })
  );
  const plusSvg = plusPoints
    .map((point) => `<text x="${roundToOneDecimal(point.x)}" y="${roundToOneDecimal(point.y)}">+</text>`)
    .join('');

  let viewBox = `0 0 ${exportWidth} ${exportHeight}`;
  let widthAttr = `${exportWidth}`;
  let heightAttr = `${exportHeight}`;
  if (cropped) {
    const extents = visualExtents({
      positions: exportPositions,
      labelBoxes,
      chargedAtoms: atoms.filter(
        (atom) => atom.formalCharge !== 0 || atom.resonanceCharge !== undefined || (atom.radicalElectronCount ?? 0) > 0
      ),
      chargeAllowance: Math.max(8, 15 * labelScale),
      strokeAllowance: bondStrokeWidth / 2 + wedgeHalfWidth,
      annotations: renderables,
      toExport: toExportAnnotationPoint,
      strokeWidth: bondStrokeWidth,
      arrowheadAllowance: arrowheadMarkerSpec('arrowhead-full').length * labelScale,
      extraPoints: [
        ...repeatBrackets.flatMap((unit) => [
          ...unit.strokes.flat(),
          { x: unit.labelBox.x, y: unit.labelBox.y },
          { x: unit.labelBox.x + unit.labelBox.width, y: unit.labelBox.y + unit.labelBox.height }
        ]),
        ...plusPoints.flatMap((point) => [
          { x: point.x - plusFontSize / 2, y: point.y - plusFontSize / 2 },
          { x: point.x + plusFontSize / 2, y: point.y + plusFontSize / 2 }
        ])
      ],
      measure: options.textOutlines ? exportFontMeasure(options.textOutlines, estimateTextWidth) : estimateTextWidth
    });
    const pad = options.paddingPx ?? 0;
    const boxWidth = Math.max(1, extents.maxX - extents.minX + pad * 2);
    const boxHeight = Math.max(1, extents.maxY - extents.minY + pad * 2);
    viewBox = `${roundToOneDecimal(extents.minX - pad)} ${roundToOneDecimal(extents.minY - pad)} ${roundToOneDecimal(boxWidth)} ${roundToOneDecimal(boxHeight)}`;
    widthAttr = options.physicalSize
      ? `${roundToTwoDecimals(boxWidth * MM_PER_PX)}mm`
      : `${roundToOneDecimal(boxWidth)}`;
    heightAttr = options.physicalSize
      ? `${roundToTwoDecimals(boxHeight * MM_PER_PX)}mm`
      : `${roundToOneDecimal(boxHeight)}`;
  }
  if (options.pageBounds) {
    const { x: pageX = 0, y: pageY = 0, width: pageWidth, height: pageHeight } = options.pageBounds;
    viewBox = `${roundToOneDecimal(pageX)} ${roundToOneDecimal(pageY)} ${roundToOneDecimal(pageWidth)} ${roundToOneDecimal(pageHeight)}`;
    widthAttr = `${roundToTwoDecimals(pageWidth * MM_PER_PX)}mm`;
    heightAttr = `${roundToTwoDecimals(pageHeight * MM_PER_PX)}mm`;
  }
  if (options.sizeMm) {
    widthAttr = `${roundToTwoDecimals(options.sizeMm.width)}mm`;
    heightAttr = `${roundToTwoDecimals(options.sizeMm.height)}mm`;
  }
  const highlight = options.highlight;
  const highlightColor = escapeXmlAttribute(highlight?.color ?? MATCH_HIGHLIGHT_COLOR);
  // #961 (owner): the match is a broad band along its bonds (7× the bond width), with no circles
  // on the atoms. Only an atom matched with none of its bonds (a one-atom match) gets a dot, of
  // the band's width, so the match still shows.
  const highlightWidth = bondStrokeWidth * 7;
  const atomsOnHighlightedBonds = new Set(
    document.structure.graph.edges
      .filter((edge) => highlight?.bondIds.has(edge.id))
      .flatMap((edge) => [edge.source, edge.target])
  );
  const highlightSvg = highlight
    ? [
        ...document.structure.graph.edges.flatMap((edge) => {
          if (!highlight.bondIds.has(edge.id)) return [];
          const from = exportPositions.get(edge.source);
          const to = endOf(edge);
          if (!from || !to) return [];
          return [
            `<line x1="${roundToOneDecimal(from.x)}" y1="${roundToOneDecimal(from.y)}" x2="${roundToOneDecimal(to.x)}" y2="${roundToOneDecimal(to.y)}" />`
          ];
        }),
        ...[...highlight.atomIds].flatMap((atomId) => {
          const at = exportPositions.get(atomId);
          return at && !atomsOnHighlightedBonds.has(atomId)
            ? [
                `<circle cx="${roundToOneDecimal(at.x)}" cy="${roundToOneDecimal(at.y)}" r="${roundToOneDecimal(highlightWidth / 2)}" stroke="none" />`
              ]
            : [];
        })
      ].join('')
    : '';
  const [vbX, vbY, vbW, vbH] = viewBox.split(' ');
  const backdrop =
    typeof options.background === 'string'
      ? `<rect x="${vbX}" y="${vbY}" width="${vbW}" height="${vbH}" fill="${escapeXmlAttribute(options.background)}" />`
      : '';

  const cycleFillsSvg = (document.depiction.cycleFills ?? [])
    .map((fill) => {
      const points = fill.atomIds.map((id) => exportPositions.get(id));
      if (points.some((point) => !point)) return '';
      return `<polygon points="${points.map((point) => `${point!.x},${point!.y}`).join(' ')}" fill="${escapeXmlAttribute(fill.color)}" stroke="none" />`;
    })
    .join('');
  // #780 -- written here, with the other labels (label() is defined above by now).
  const stereoLabelsSvg = stereoLabelSpots.length
    ? `\n    <g class="stereo-labels">${stereoLabelSpots
        .map((spot) => label(spot.text, spot.x, spot.y, stereoFontSize, ' font-style="italic" font-weight="600"'))
        .join('')}</g>`
    : '';
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${widthAttr}" height="${heightAttr}" viewBox="${viewBox}" preserveAspectRatio="xMidYMid meet">${backdrop ? `\n  ${backdrop}` : ''}${
    backgroundAnnotationsSvg
      ? `\n  <g class="background-annotations" fill="none" stroke="${color}" stroke-width="${bondStrokeWidth}" stroke-linecap="round" stroke-linejoin="round">${backgroundAnnotationsSvg}</g>`
      : ''
  }${
    highlightSvg
      ? `\n  <g class="match-highlight" fill="${highlightColor}" stroke="${highlightColor}" stroke-width="${roundToOneDecimal(highlightWidth)}" stroke-linecap="round" opacity="0.45">${highlightSvg}</g>`
      : ''
  }
  <g class="cycle-fills">${cycleFillsSvg}</g>${
    plusSvg
      ? `\n  <g class="reaction-plus-signs" fill="${color}" text-anchor="middle" dominant-baseline="central" font-family="Arial, sans-serif" font-size="${plusFontSize}">${plusSvg}</g>`
      : ''
  }
  ${arrowheadDefs(color, labelScale, markerIdPrefix)}
  <g fill="none" stroke="${color}" stroke-width="${bondStrokeWidth}" stroke-linecap="round">
    ${bondElements}
    ${wedges}
  </g>
  <g fill="${color}" text-anchor="middle" dominant-baseline="central" font-family="Arial, sans-serif" font-size="${labelFontSize}" font-weight="600">
    ${labels}
    ${bracketLabels}${stereoLabelsSvg}
  </g>
  <g fill="none" stroke="${color}" stroke-width="${bondStrokeWidth}" stroke-linecap="round" stroke-linejoin="round">
    ${annotationsSvg}
  </g>
</svg>\n`;
}
